import { useContext, useState } from "react";
import { Screen } from "../../../main/components/screen/Screen";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { MainContext } from "../../../main/context/MainContextProvider";
import { usinasApi } from "../../../services/api";
import { ErrorModal } from "../../../main/components/ErrorModal";
import { NotFoundError } from "../../../errors";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import DatePickerLocalizationAdapter from "../../../main/context/DatePickerLocalizationAdapter";
import { useNavigate } from "react-router-dom";
import { transformDateFormat } from "../../dashboard/helpers/format-number";
import { formatDateAndHour, isNumberNullOrNegative, numberPtBr, numberPtBrPercent0100 } from "../../../main/utils/utils";
import CardBox from "../../../main/components/CardBox";
import { UsinaDetailsCard, UsinaDetailsInfo } from "./ReportDaily";
import ParetoRelatorioParcialChart from "../components/report_cards/ParetoRelatorioParcial";
import FalhasCriticasAtivasChart from "../components/report_cards/FalhasCriticasAtivasChart";
import RankingUsinasChart from "../components/report_cards/RankingUsinasChart";
import FalhasCriticasAtivasTable from "../components/report_cards/FalhasCriticasAtivasTable";

export default function PartialReport() {
    const {
        toastNotifications,
        setToastNotifications,
        relatorioParcialLoading,
        relatorioParcial,
        selectedDashboardClient: selectedClient,
        user,
        setRelatorioParcialLoading,
        setRelatorioParcial
    } = useContext(MainContext);

    const [isLoadingPartialReport, setIsLoadingPartialReport] =
        useState<boolean>(false);

    const [errorModalData, setErrorModalData] = useState<{
        title: string;
        message: string;
    }>({ title: "", message: "" });

    const handleGetPartialReport = async () => {
        setIsLoadingPartialReport(true);
        try {
            const pdfResponse = await usinasApi.getPartialReportPdf(
                reportDate,
                selectedClient?.id ?? "",
                user?.userId ?? ""
            );

            // Download by creating a link and clicking it to work on all browsers
            const downloadButton = document.createElement("a");
            downloadButton.href = pdfResponse.data.url;
            downloadButton.download = `relatorio-parcial-${reportDate}.pdf`;
            downloadButton.click();
        } catch (error) {
            if (error instanceof NotFoundError) {
                setErrorModalData({
                    title: "Relatório não encontrado",
                    message: `O relatório do dia ${reportDate
                        .split("-")
                        .reverse()
                        .join("/")
                        .replace(/\/\//g, "/")} não está disponível.
            Tente gerar o relatório antes de buscar o pdf.`,
                });
                return;
            }
            setErrorModalData({
                title: "Erro ao buscar pdf",
                message: "Ocorreu um erro ao buscar o pdf, tente novamente.",
            });
        } finally {
            setIsLoadingPartialReport(false);
        }
    };

    const closeErrorModal = (open: boolean) => {
        !open && setErrorModalData({ title: "", message: "" });
    };
    const showErrorModal = errorModalData.title !== "";

    const today = dayjs(new Date());

    const [reportDate, setReportDate] = useState<string>(
        new Date().toISOString().split("T")[0]
    );
    const [reportErrors, setReportErrors] = useState<string[]>([]);
    const dayjsDate = reportDate ? dayjs(reportDate) : null;

    const navigate = useNavigate();

    const getRelatorioParcial = async (reportDate: string) => {
        setRelatorioParcialLoading(true);
        const newNotifications = {
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "A busca foi inicializada, voce será notificado assim que ela for finalizada!",
                },
            ],
        };
        setToastNotifications(newNotifications);
        try {
            const reportPartialResponse = await usinasApi.getPartialReportInfo(
                reportDate,
                selectedClient?.id ?? "",
                user?.userId ?? ""
            );

            setRelatorioParcial(reportPartialResponse.data.relatorioParcial);
            setReportErrors(reportPartialResponse.data.errors);
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "success" as const,
                        text: (
                            <>
                                Relatório parcial gerado com sucesso!{" "}
                                <a
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/usinas/boletim-Parcial")}
                                >
                                    Clique aqui
                                </a>{" "}
                                para vizualizar.
                            </>
                        ),
                    },
                ],
            };
            setToastNotifications(newNotifications);
        } catch (error) {
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "error" as const,
                        text: "Erro ao gerar relatório parcial, tente novamente!",
                    },
                ],
            };
            setToastNotifications(newNotifications);
        } finally {
            setRelatorioParcialLoading(false);
        }
    };

    const getExportButtonTooltipText = (): React.ReactNode => {

        if (isLoadingPartialReport) return "Aguarde a exportação do relatório parcial."

        if (!reportDate) return "Selecione uma data para geração de relatório."


        return "";
    }

    const getGenerateButtonTooltipText = (): React.ReactNode => {

        if (relatorioParcialLoading) return "Aguarde a busca do relatório parcial."

        if (!reportDate) return "Selecione uma data para geração de relatório."

        return "";
    }

    const disponibilidadeMedia = isNumberNullOrNegative(
        relatorioParcial?.dadosUsinas.disponibilidade
    )
        ? "-"
        : `${numberPtBrPercent0100(
            relatorioParcial?.dadosUsinas.disponibilidade ?? 0,
            1,
            0
        )}`;

    const prRealizado =
        isNumberNullOrNegative(
            relatorioParcial?.dadosUsinas.prRealizado
        ) ||
            (relatorioParcial?.dadosUsinas.prRealizado ?? 0) > 150
            ? "-"
            : `${relatorioParcial?.dadosUsinas.prRealizado.toFixed(2) ?? 0
            }`;

    const energiaGerada = isNumberNullOrNegative(
        relatorioParcial?.dadosUsinas.energiaGerada
    )
        ? 0
        : `${numberPtBr(relatorioParcial?.dadosUsinas?.energiaGerada ?? 0 / 1000,
            1
        )} MWh`

    const energiaAcumulada = isNumberNullOrNegative(
        relatorioParcial?.dadosUsinas.energiaAcumulada
    )
        ? 0
        : `${numberPtBr(relatorioParcial?.dadosUsinas?.energiaAcumulada ?? 0 / 1000,
            1
        )} MWh`

    // const irradiacaoRealizada = isNumberNullOrNegative(
    //     relatorioParcial?.dadosUsinas.irradianciaRealizada
    // )
    //     ? "-"
    //     : `${numberPtBrPercent0100(
    //         relatorioParcial?.dadosUsinas.irradianciaRealizada ?? 0,
    //         1,
    //         0
    //     )}`;

    return (
        <Screen.Root
            sx={{
                padding: "20px",
                height: "100vh",
                display: "flex",
                overflow: "auto",
                alignContent: "center",
            }}
        >
            <Screen.Container removePadding sx={{ height: "100%" }}>
                <Grid
                    container
                    sx={{
                        height: "100%",
                        paddingBottom: "16px",
                    }}
                    spacing={2}
                >
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "20px",
                            alignItems: "center",
                            justifyContent: relatorioParcial ? undefined : "center",
                        }}
                    >
                        <Box
                            sx={{
                                maxWidth: "400px",
                                minWidth: { xs: "250px", md: "400px" },
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                Relatório Parcial
                            </Typography>
                            <DatePickerLocalizationAdapter>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    value={dayjsDate}
                                    onChange={(e) => e && setReportDate(e.format("YYYY-MM-DD"))}
                                    maxDate={today}
                                    label={"Data do Relatório"}
                                    sx={{
                                        width: "100%",
                                        marginY: "12px",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "4px",
                                        },
                                    }}
                                />
                            </DatePickerLocalizationAdapter>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", md: "row" },
                                    width: "100%",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Tooltip
                                    title={getExportButtonTooltipText()}
                                >
                                    <Box
                                        sx={{
                                            width: { sx: "100%", md: "49%" },
                                            marginBottom: { xs: "8px", md: "0px" },
                                        }}
                                    >
                                        <PrimaryBaseButton
                                            disabled={
                                                isLoadingPartialReport ||
                                                !reportDate
                                            }
                                            sx={{ width: "100%" }}
                                            loading={isLoadingPartialReport}
                                            title="Exportar PDF"
                                            onClick={handleGetPartialReport}
                                        />
                                    </Box>
                                </Tooltip>
                                <Tooltip
                                    title={getGenerateButtonTooltipText()}
                                >
                                    <Box sx={{ width: { sx: "100%", md: "49%" } }}>
                                        <PrimaryBaseButton
                                            disabled={
                                                !reportDate ||
                                                relatorioParcialLoading
                                            }
                                            sx={{ width: "100%" }}
                                            loading={relatorioParcialLoading}
                                            title="Gerar Relatório"
                                            onClick={() => getRelatorioParcial(reportDate)}
                                        />
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>
                    {relatorioParcial && (
                        <>
                            <Grid sx={{ width: "100%" }} item xs={12}>
                                <Typography variant="h6" sx={{ paddingBottom: "20px" }}>
                                    {`${selectedClient?.name} - ${transformDateFormat(
                                        relatorioParcial.dataDeReferencia
                                    )}`}
                                </Typography>
                            </Grid>
                            {reportErrors.map((error, i) => (
                                <Grid key={i} sx={{ width: "100%" }} item xs={12}>
                                    <Typography
                                        sx={{
                                            paddingBottom: "10px",
                                            color: "#ff6060",
                                            fontSize: 14,
                                        }}
                                    >
                                        {error}
                                    </Typography>
                                </Grid>
                            ))}
                            <Grid sx={{ width: "100%" }} item xs={12}>
                                <CardBox
                                    dateLeft={`Dados do relatório coletados em: ${formatDateAndHour(
                                        relatorioParcial.dataDeGeracao
                                    )}`}
                                    leftTopCalc={290}
                                    widthTopCalc="305"
                                    sx={{
                                        height: "100%",
                                        width: "100%",
                                        paddingY: "32px",
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: "16px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexDirection: "column",
                                            display: "flex",
                                            justifyContent: "space-around",
                                            width: "100%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flexDirection: { xs: "column", md: "row" },
                                                display: "flex",
                                                justifyContent: "space-around",
                                            }}
                                        >
                                            <UsinaDetailsInfo
                                                title="Energia Gerada: "
                                                value={energiaGerada}
                                            />
                                            <UsinaDetailsInfo
                                                title="Número de Inversores: "
                                                value={
                                                    relatorioParcial.dadosUsinas?.numeroInversores ?? 0
                                                }
                                            />
                                            <UsinaDetailsInfo
                                                title="Número de Módulos: "
                                                value={relatorioParcial.dadosUsinas?.numeroModulos ?? 0}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                flexDirection: { xs: "column", md: "row" },
                                                display: "flex",
                                                justifyContent: "space-around",
                                            }}
                                        >
                                            <UsinaDetailsInfo
                                                title="Potência Nominal: "
                                                value={`${numberPtBr(
                                                    relatorioParcial.dadosUsinas?.potenciaNominal ?? 0 / 1000,
                                                    1
                                                )} MW`}
                                            />
                                            <UsinaDetailsInfo
                                                title="Potência Média: "
                                                value={`${numberPtBr(
                                                    relatorioParcial.dadosUsinas?.potenciaMedia ?? 0 / 1000,
                                                    1
                                                )} MW`}
                                            />
                                            <UsinaDetailsInfo
                                                title="Disponibilidade Média: "
                                                value={disponibilidadeMedia}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            flexDirection: { xs: "column", md: "row" },
                                            display: "flex",
                                            width: "100%",
                                            justifyContent: "space-around",
                                        }}
                                    >
                                        <UsinaDetailsCard
                                            title="PR"
                                            value={prRealizado}
                                        />
                                        <UsinaDetailsCard
                                            title="Energia do Dia"
                                            value={energiaGerada}
                                        />
                                        <UsinaDetailsCard
                                            title="Energia Acumulada"
                                            value={energiaAcumulada}
                                        />
                                        <UsinaDetailsCard
                                            title="Disponibilidade Média"
                                            value={disponibilidadeMedia}
                                        />
                                    </Box>
                                </CardBox>
                            </Grid>
                            {relatorioParcial.falhasCriticasAtivas && (
                                <>
                                    <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                                        <CardBox
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                minHeight: "400px",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <FalhasCriticasAtivasChart
                                                data={relatorioParcial.falhasCriticasAtivas}
                                            />
                                        </CardBox>
                                    </Grid>
                                    <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                                        <FalhasCriticasAtivasTable
                                            falhasCriticasAtivas={relatorioParcial.falhasCriticasAtivas}
                                            alarmDescriptionByCode={relatorioParcial.alarmDescriptionByCode}
                                        />
                                    </Grid>
                                </>
                            )}
                            {relatorioParcial.paretoPerformanceRatio && (
                                <>
                                    <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                                        <CardBox
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                minHeight: "400px",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <ParetoRelatorioParcialChart
                                                title="Pareto de Performance Ratio"
                                                serieTitle="Performance Ratio"
                                                data={relatorioParcial.paretoPerformanceRatio}
                                                yAxisNameList={[""]}
                                            />
                                        </CardBox>
                                    </Grid>
                                </>
                            )}
                            {relatorioParcial.paretoDisponibilidade && (
                                <>
                                    <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                                        <CardBox
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                minHeight: "400px",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <ParetoRelatorioParcialChart
                                                title="Pareto de Disponibilidade"
                                                serieTitle="Disponibilidade"
                                                data={relatorioParcial.paretoDisponibilidade}
                                            />
                                        </CardBox>
                                    </Grid>
                                </>
                            )}
                            {relatorioParcial.rankingUsinas && (
                                <>
                                    <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                                        <CardBox
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                minHeight: "400px",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <RankingUsinasChart
                                                rankingUsinas={relatorioParcial.rankingUsinas}
                                            />
                                        </CardBox>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </Grid>
            </Screen.Container>
            <ErrorModal
                contentText={errorModalData.message}
                open={showErrorModal}
                setOpen={closeErrorModal}
                title={errorModalData.title}
            />
        </Screen.Root>
    );
}
